import { useNuxtApp, useState } from "#imports";
import { useLoginResponse } from "~/composables/useLoginResponse";

const SESSION_AUTH_KEY = "apsh_auth_authorized";
const SESSION_RETURN_TO_KEY = "apsh_auth_return_to";
const LOCAL_GUID_KEY = "guid";
const LOCAL_LOGIN_RESPONSE_KEY = "login_response";
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

let bootstrapPromise: Promise<void> | null = null;

const normalizeGuid = (value: unknown): string => {
  if (typeof value !== "string") return "";
  const normalized = value.trim();
  return UUID_REGEX.test(normalized) ? normalized : "";
};

const normalizeRedirectPath = (value: unknown): string => {
  if (typeof value !== "string") return "";
  const normalized = value.trim();
  if (!normalized || !normalized.startsWith("/")) return "";

  const [rawPath = "/", suffix = ""] = normalized.split(/([?#].*)/, 2);
  const path = rawPath === "/" ? "/" : rawPath.replace(/\/+$/, "") || "/";
  const canonical = `${path}${suffix}`;

  if (path === "/login") return "";
  if (path === "/register") {
    return "";
  }
  return canonical;
};

const findGuidInPayload = (
  payload: unknown,
  seen = new Set<unknown>(),
): string => {
  const directGuid = normalizeGuid(payload);
  if (directGuid) return directGuid;

  if (!payload || typeof payload !== "object" || seen.has(payload)) return "";
  seen.add(payload);

  if (Array.isArray(payload)) {
    for (const item of payload) {
      const found = findGuidInPayload(item, seen);
      if (found) return found;
    }
    return "";
  }

  const record = payload as Record<string, unknown>;
  const preferredKeys = ["guid", "GUID", "Guid", "Идентификатор"];

  for (const key of preferredKeys) {
    if (!(key in record)) continue;
    const found = findGuidInPayload(record[key], seen);
    if (found) return found;
  }

  for (const value of Object.values(record)) {
    const found = findGuidInPayload(value, seen);
    if (found) return found;
  }

  return "";
};

const readGuidFromStorage = (): string => {
  if (!process.client) return "";

  const directGuid = normalizeGuid(localStorage.getItem(LOCAL_GUID_KEY));
  if (directGuid) return directGuid;

  try {
    const raw = localStorage.getItem(LOCAL_LOGIN_RESPONSE_KEY);
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    const extracted = findGuidInPayload(parsed);
    if (extracted) {
      localStorage.setItem(LOCAL_GUID_KEY, extracted);
    }
    return extracted;
  } catch (error) {
    console.error("Failed to parse saved login response", error);
    return "";
  }
};

export const useAuthBootstrap = () => {
  const isAuthorized = useState<boolean>("auth:is-authorized", () => false);
  const isBootstrapping = useState<boolean>(
    "auth:is-bootstrapping",
    () => false,
  );
  const hasCheckedBootstrap = useState<boolean>(
    "auth:has-checked-bootstrap",
    () => false,
  );
  const isHydrated = useState<boolean>("auth:is-hydrated", () => false);

  const syncSessionFlags = () => {
    if (!process.client) return;

    if (isAuthorized.value) {
      sessionStorage.setItem(SESSION_AUTH_KEY, "1");
    } else {
      sessionStorage.removeItem(SESSION_AUTH_KEY);
    }
  };

  const hydrateFromSession = () => {
    if (!process.client || isHydrated.value) return;

    isAuthorized.value = sessionStorage.getItem(SESSION_AUTH_KEY) === "1";
    isHydrated.value = true;
  };

  const markAuthorized = () => {
    isAuthorized.value = true;
    hasCheckedBootstrap.value = true;
    if (process.client) {
      sessionStorage.removeItem(SESSION_RETURN_TO_KEY);
    }
    syncSessionFlags();
  };

  const markUnauthorized = () => {
    isAuthorized.value = false;
    syncSessionFlags();
  };

  const markBootstrapChecked = () => {
    hasCheckedBootstrap.value = true;
    syncSessionFlags();
  };

  const rememberReturnTo = (path?: unknown) => {
    if (!process.client) return;
    const normalized = normalizeRedirectPath(path);
    if (!normalized) return;
    sessionStorage.setItem(SESSION_RETURN_TO_KEY, normalized);
  };

  const consumeReturnTo = (queryValue?: unknown): string => {
    if (process.client) {
      const storedPath = normalizeRedirectPath(
        sessionStorage.getItem(SESSION_RETURN_TO_KEY),
      );
      sessionStorage.removeItem(SESSION_RETURN_TO_KEY);

      const queryCandidate = Array.isArray(queryValue)
        ? queryValue[0]
        : queryValue;
      const fromQuery = normalizeRedirectPath(queryCandidate);
      return fromQuery || storedPath || "/";
    }

    const queryCandidate = Array.isArray(queryValue)
      ? queryValue[0]
      : queryValue;
    const fromQuery = normalizeRedirectPath(queryCandidate);
    if (fromQuery) return fromQuery;
    return "/";
  };

  const persistGuid = (value: unknown): string => {
    if (!process.client) return "";
    const guid = normalizeGuid(value);
    if (!guid) return "";
    localStorage.setItem(LOCAL_GUID_KEY, guid);
    return guid;
  };

  const persistGuidFromLoginResponse = (payload: unknown): string => {
    const extracted = findGuidInPayload(payload);
    if (!extracted) return "";
    return persistGuid(extracted);
  };

  const ensureBootstrap = async (returnTo?: unknown) => {
    if (!process.client) return;
    hydrateFromSession();

    if (hasCheckedBootstrap.value) return;
    if (bootstrapPromise) return bootstrapPromise;

    const nuxtApp = useNuxtApp();
    const $useOneCApi = nuxtApp.$useOneCApi as (
      action: string,
      payload?: Record<string, any>,
    ) => Promise<unknown>;
    const { persistLoginResponse } = useLoginResponse();

    bootstrapPromise = (async () => {
      isBootstrapping.value = true;
      try {
        const guid = readGuidFromStorage();
        if (!guid) {
          markUnauthorized();
          rememberReturnTo(returnTo);
          return;
        }

        const response = await $useOneCApi("loginUser", { guid });
        persistLoginResponse(response);
        markAuthorized();
      } catch (error) {
        markUnauthorized();
        rememberReturnTo(returnTo);
      } finally {
        markBootstrapChecked();
        isBootstrapping.value = false;
        bootstrapPromise = null;
      }
    })();

    return bootstrapPromise;
  };

  return {
    isAuthorized,
    isBootstrapping,
    hasCheckedBootstrap,
    hydrateFromSession,
    ensureBootstrap,
    markAuthorized,
    markUnauthorized,
    rememberReturnTo,
    consumeReturnTo,
    persistGuid,
    persistGuidFromLoginResponse,
    readGuidFromStorage,
  };
};
